/**
 * Infinite logo marquee.
 * Never replaces the original logo markup — only appends clones.
 *
 * 1. Repeat the logo set until it covers the viewport
 * 2. Clone that strip once for a seamless seam
 * 3. Animate by exact content width + gap (px)
 */
class LogoSliderMarquee {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-logo-slider-track]');
    this.content = root.querySelector('[data-logo-slider-content]');
    this.items = root.querySelector('[data-logo-slider-items]');
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartTime = 0;
    this.seamClone = null;
    this.maxFills = 8;
    this.building = false;

    this.pauseButton = root.closest('.logo-slider')?.querySelector('[data-logo-slider-pause]') ?? null;
    this.nameTip = root.closest('.logo-slider')?.querySelector('[data-logo-slider-name]') ?? null;
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onPauseClick = this.onPauseClick.bind(this);
    this.onLogoFocusIn = this.onLogoFocusIn.bind(this);
    this.onLogoFocusOut = this.onLogoFocusOut.bind(this);
    this.onResize = this.onResize.bind(this);
    this.build = this.build.bind(this);
  }

  init() {
    if (!this.track || !this.content || !this.items) return;
    if (this.items.children.length < 1) return;

    this.labelLogos();
    this.items.addEventListener('focusin', this.onLogoFocusIn);
    this.items.addEventListener('focusout', this.onLogoFocusOut);

    if (this.prefersReducedMotion || this.root.dataset.autoplay !== 'true') {
      this.root.dataset.disabled = 'true';
      return;
    }

    // Prefer eager load so widths are available for the seam measure.
    this.items.querySelectorAll('img').forEach((img) => {
      img.loading = 'eager';
      if (!img.complete) {
        img.addEventListener('load', this.build, { once: true });
      }
    });

    this.build();

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.root);
    }

    this.root.addEventListener('pointerdown', this.onPointerDown);
    this.root.addEventListener('keydown', this.onKeyDown);
    this.pauseButton?.addEventListener('click', this.onPauseClick);
  }

  destroy() {
    this.resizeObserver?.disconnect();
    window.clearTimeout(this.resizeTimer);
    window.clearTimeout(this.keyResumeTimer);
    this.root.removeEventListener('pointerdown', this.onPointerDown);
    this.root.removeEventListener('pointermove', this.onPointerMove);
    this.root.removeEventListener('pointerup', this.onPointerUp);
    this.root.removeEventListener('pointercancel', this.onPointerUp);
    this.root.removeEventListener('keydown', this.onKeyDown);
    this.pauseButton?.removeEventListener('click', this.onPauseClick);
    this.items?.removeEventListener('focusin', this.onLogoFocusIn);
    this.items?.removeEventListener('focusout', this.onLogoFocusOut);
    this.hideName();
    this.clearClones();
    this.root.style.removeProperty('--logo-slider-loop-distance');
  }

  get animation() {
    return this.track.getAnimations()[0] ?? null;
  }

  clearClones() {
    this.content.querySelectorAll('[data-logo-fill]').forEach((el) => el.remove());
    this.seamClone?.remove();
    this.seamClone = null;
  }

  getTrackGap() {
    const styles = window.getComputedStyle(this.track);
    return Number.parseFloat(styles.columnGap || styles.gap) || 0;
  }

  build() {
    if (this.building) return;
    this.building = true;
    this.resizeObserver?.disconnect();

    this.clearClones();

    const marqueeWidth = this.root.clientWidth;
    if (marqueeWidth <= 0) {
      this.building = false;
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(this.build, 150);
      return;
    }

    // Append copies of the original items group until the strip covers the viewport.
    let fills = 0;
    while (this.content.scrollWidth < marqueeWidth * 1.25 && fills < this.maxFills) {
      const fill = this.items.cloneNode(true);
      fill.setAttribute('data-logo-fill', '');
      fill.setAttribute('aria-hidden', 'true');
      fill.querySelectorAll('a[href]').forEach((link) => link.setAttribute('tabindex', '-1'));
      this.silenceClone(fill);
      this.content.appendChild(fill);
      fills += 1;
    }

    // Second half for a seamless loop — originals stay untouched.
    const seam = this.content.cloneNode(true);
    seam.setAttribute('aria-hidden', 'true');
    seam.removeAttribute('data-logo-slider-content');
    seam.querySelectorAll('a[href]').forEach((link) => link.setAttribute('tabindex', '-1'));
    this.silenceClone(seam);
    this.track.appendChild(seam);
    this.seamClone = seam;

    const gap = this.getTrackGap();
    const contentWidth = this.content.getBoundingClientRect().width;
    const loopDistance = contentWidth + gap;

    if (loopDistance > 0) {
      this.root.style.setProperty('--logo-slider-loop-distance', `${loopDistance.toFixed(2)}px`);
    }

    this.building = false;

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.root);
    }
  }

  onResize() {
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(this.build, 150);
  }

  onPointerDown(event) {
    if (event.button !== 0 || this.root.dataset.disabled === 'true') return;
    if (event.target.closest('a[href]')) return;

    this.isDragging = true;
    this.root.dataset.dragging = 'true';
    this.root.setPointerCapture(event.pointerId);
    this.dragStartX = event.clientX;

    const animation = this.animation;
    if (animation) {
      animation.pause();
      this.dragStartTime = Number(animation.currentTime) || 0;
    }

    this.root.addEventListener('pointermove', this.onPointerMove);
    this.root.addEventListener('pointerup', this.onPointerUp);
    this.root.addEventListener('pointercancel', this.onPointerUp);
  }

  onPointerMove(event) {
    if (!this.isDragging) return;
    const animation = this.animation;
    if (!animation) return;
    animation.currentTime = Math.max(0, this.dragStartTime - (event.clientX - this.dragStartX));
  }

  onPointerUp(event) {
    if (!this.isDragging) return;

    this.isDragging = false;
    delete this.root.dataset.dragging;

    if (this.root.hasPointerCapture(event.pointerId)) {
      this.root.releasePointerCapture(event.pointerId);
    }

    this.root.removeEventListener('pointermove', this.onPointerMove);
    this.root.removeEventListener('pointerup', this.onPointerUp);
    this.root.removeEventListener('pointercancel', this.onPointerUp);

    if (this.animation && this.root.dataset.autoplay === 'true' && this.root.dataset.paused !== 'true' && this.root.dataset.logoFocus !== 'true') {
      this.animation.play();
    }
  }

  logoName(img) {
    const written = (img.getAttribute('alt') || '').replace(/\s+/g, ' ').trim();
    if (written) return written;

    const src = `${img.currentSrc || ''} ${img.getAttribute('src') || ''}`.toLowerCase();
    const known = [
      ['scotrail', 'ScotRail'],
      ['cs-logo', 'Caledonian Sleeper'],
      ['nhs-scotland', 'NHS Scotland'],
      ['mod-logo', 'Ministry of Defence'],
      ['pwc-logo', 'PwC'],
      ['baesystems', 'BAE Systems'],
    ];
    const match = known.find(([key]) => src.includes(key));
    return match ? match[1] : '';
  }

  labelLogos() {
    this.items.querySelectorAll('.image-block img').forEach((img) => {
      const name = this.logoName(img);
      if (!name) {
        img.removeAttribute('tabindex');
        return;
      }
      img.alt = name;
      img.tabIndex = 0;
    });
  }

  silenceClone(root) {
    root.querySelectorAll('img').forEach((img) => {
      img.tabIndex = -1;
    });
  }

  onLogoFocusIn(event) {
    const img = event.target instanceof HTMLImageElement ? event.target : null;
    if (!img || !this.items.contains(img)) return;

    this.root.dataset.logoFocus = 'true';
    this.track.style.animationPlayState = 'paused';
    this.animation?.pause();
    this.revealLogo(img);
  }

  onLogoFocusOut(event) {
    const next = event.relatedTarget;
    if (next instanceof Node && this.items.contains(next)) return;

    delete this.root.dataset.logoFocus;
    this.hideName();

    if (this.root.dataset.paused === 'true' || this.root.dataset.disabled === 'true') return;
    if (this.root.dataset.autoplay !== 'true') return;

    this.track.style.animationPlayState = '';
    this.animation?.play();
  }

  revealLogo(img) {
    const animation = this.animation;
    const marqueeBox = this.root.getBoundingClientRect();
    const box = img.getBoundingClientRect();
    const padding = 16;
    const inView = box.left >= marqueeBox.left + padding && box.right <= marqueeBox.right - padding;

    if (!inView && animation) {
      const distance = Number.parseFloat(
        getComputedStyle(this.root).getPropertyValue('--logo-slider-loop-distance')
      );
      const duration = Number(animation.effect?.getTiming?.().duration) || Number(this.root.dataset.loopDuration);

      if (distance > 0 && duration > 0) {
        const deltaPx = box.left - (marqueeBox.left + padding);
        const next = (Number(animation.currentTime) || 0) + (deltaPx / distance) * duration;
        animation.currentTime = ((next % duration) + duration) % duration;
      }
    }

    this.showName(img);
  }

  showName(img) {
    const tip = this.nameTip;
    const name = (img.getAttribute('alt') || '').replace(/\s+/g, ' ').trim();
    if (!tip || !name) return;

    tip.hidden = false;
    tip.textContent = name;

    const stage = tip.offsetParent || tip.parentElement;
    const stageBox = stage.getBoundingClientRect();
    const box = img.getBoundingClientRect();
    const tipWidth = tip.offsetWidth;
    const min = tipWidth / 2 + 8;
    const max = Math.max(min, stageBox.width - tipWidth / 2 - 8);
    const left = Math.min(max, Math.max(min, box.left + box.width / 2 - stageBox.left));

    tip.style.left = `${left}px`;
    tip.style.top = `${box.top - stageBox.top - 8}px`;
  }

  hideName() {
    if (!this.nameTip) return;
    this.nameTip.hidden = true;
    this.nameTip.textContent = '';
  }

  onPauseClick() {
    const pausing = this.root.dataset.paused !== 'true';

    if (pausing) {
      this.root.dataset.paused = 'true';
      this.track.style.animationPlayState = 'paused';
      this.animation?.pause();
    } else {
      delete this.root.dataset.paused;
      this.track.style.animationPlayState = '';
      if (this.root.dataset.autoplay === 'true' && this.root.dataset.logoFocus !== 'true') {
        this.animation?.play();
      }
    }

    const button = this.pauseButton;
    if (!button) return;

    button.setAttribute('aria-pressed', pausing ? 'true' : 'false');
    button.textContent = pausing ? button.dataset.labelPlay : button.dataset.labelPause;
  }

  onKeyDown(event) {
    if (this.root.dataset.disabled === 'true') return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onPauseClick();
      return;
    }

    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;

    event.preventDefault();
    const animation = this.animation;
    if (!animation) return;

    animation.pause();
    const currentTime = Number(animation.currentTime) || 0;
    animation.currentTime = Math.max(
      0,
      event.key === 'ArrowLeft' ? currentTime - 48 : currentTime + 48
    );

    if (this.root.dataset.autoplay === 'true' && this.root.dataset.paused !== 'true' && this.root.dataset.logoFocus !== 'true') {
      const resumeDelay = Number(this.root.dataset.transitionSpeed) || 500;
      window.clearTimeout(this.keyResumeTimer);
      this.keyResumeTimer = window.setTimeout(() => animation.play(), resumeDelay);
    }
  }
}

class LogoSliderComponent extends HTMLElement {
  connectedCallback() {
    this._marquee?.destroy();
    this._marquee = new LogoSliderMarquee(this);
    this._marquee.init();
  }

  disconnectedCallback() {
    this._marquee?.destroy();
    this._marquee = null;
  }
}

if (!customElements.get('logo-slider-component')) {
  customElements.define('logo-slider-component', LogoSliderComponent);
}

if (!window.__logoSliderKeyboard) {
  window.__logoSliderKeyboard = true;

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' && !event.key.startsWith('Arrow')) return;
    document.documentElement.classList.add('is-keyboard');
  }, true);

  document.addEventListener('pointerdown', (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest('.logo-slider__pause')) return;
    document.documentElement.classList.remove('is-keyboard');
  }, true);
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('logo-slider-component').forEach((element) => {
    if (!(element instanceof LogoSliderComponent)) customElements.upgrade(element);
  });
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('logo-slider-component').forEach((element) => {
    element._marquee?.destroy();
    element._marquee = null;
  });
});
